import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Building2, CheckCircle2, AlertCircle, LogOut, KeyRound, Lock, LogIn, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/common/Logo';
import type { WorkspaceInvitation } from '@sahaj/types';

export const AcceptInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, login } = useAuth();
  const token = searchParams.get('token') || '';

  const [invitation, setInvitation] = useState<WorkspaceInvitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Activation form fields (for provisioned employees)
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Missing invitation token in URL.');
      setIsLoading(false);
      return;
    }

    api.invitations.preview(token)
      .then((inv) => {
        setInvitation(inv);
      })
      .catch((err: any) => {
        const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
        setError(serverMsg || 'Invalid or expired invitation token.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token]);

  const isEmailMatching = Boolean(
    isAuthenticated &&
    user?.email &&
    invitation?.email &&
    user.email.toLowerCase() === invitation.email.toLowerCase()
  );

  const isEmailMismatch = Boolean(
    isAuthenticated &&
    user?.email &&
    invitation?.email &&
    user.email.toLowerCase() !== invitation.email.toLowerCase()
  );

  const isProvisionedEmployee = Boolean(!invitation?.isExistingUser);

  const handleAcceptExisting = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/invite?token=${encodeURIComponent(token)}&email=${encodeURIComponent(invitation?.email || '')}`);
      return;
    }

    if (isEmailMismatch) {
      setError(`Cannot accept: this invitation was sent to ${invitation?.email}, but you are signed in as ${user?.email}.`);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await api.invitations.accept({ token });
      navigate('/app');
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to accept invitation.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivateProvisionedAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!temporaryPassword.trim() || !newPassword.trim()) return;

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const res = await api.auth.activate({
        token,
        temporaryPassword: temporaryPassword.trim(),
        newPassword
      });

      await login({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      navigate('/app');
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to activate account. Please check your temporary credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSwitchAccount = () => {
    logout();
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-[#F7F9FB] px-4 py-12 text-[#1a1b4b]">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-3">
          <Link to="/" className="inline-flex items-center justify-center">
            <Logo size="lg" />
          </Link>
          <h2 className="text-2xl font-black tracking-tight text-[#1a1b4b]">
            {isProvisionedEmployee && !isAuthenticated ? 'Activate Employee Account' : 'Workspace Invitation'}
          </h2>
        </div>

        <div className="bg-white border-2 border-[#3e4491]/20 rounded-3xl p-8 shadow-xl space-y-6 text-center">
          {isLoading ? (
            <div className="py-8 text-sm text-[#3e4491] animate-pulse">Loading invitation details...</div>
          ) : error && !invitation ? (
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto">
                <AlertCircle className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-base text-[#1a1b4b]">Invalid Invitation</h3>
              <p className="text-xs text-[#3e4491]">{error}</p>
              <Link
                to="/app"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#1a1b4b] hover:bg-[#292a73] text-white text-xs font-bold transition"
              >
                Go to Workspace
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-[#3a9efd]/10 text-[#292a73] flex items-center justify-center mx-auto shadow-sm">
                <Building2 className="h-8 w-8" />
              </div>

              <div>
                <h3 className="font-bold text-lg text-[#1a1b4b]">
                  {invitation?.invitedName ? `Welcome, ${invitation.invitedName}!` : "You've been invited to join a team!"}
                </h3>
                <p className="text-xs text-[#3e4491] mt-1">
                  {invitation?.invitedBy?.displayName
                    ? `${invitation.invitedBy.displayName} added you to collaborate.`
                    : 'Invited to collaborate on Sahaj Connect.'}
                </p>
              </div>

              <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#3e4491]/20 text-xs text-[#3e4491] flex items-center justify-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#3a9efd]" />
                <span>Account email: <strong className="text-[#1a1b4b]">{invitation?.email}</strong></span>
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-medium text-left">
                  {error}
                </div>
              )}

              {/* Case 1: Signed in with WRONG email */}
              {isEmailMismatch && (
                <div className="p-4 bg-[#f7a400]/10 border border-[#f7a400]/30 rounded-2xl text-xs text-[#1a1b4b] text-left space-y-2">
                  <div className="font-bold flex items-center gap-1.5 text-[#1a1b4b]">
                    <AlertCircle className="h-4 w-4 shrink-0 text-[#f7a400]" />
                    <span>Account Mismatch</span>
                  </div>
                  <p className="text-[#3e4491]">
                    You are signed in as <strong>{user?.email}</strong>, but this link belongs to <strong>{invitation?.email}</strong>.
                  </p>
                  <button
                    onClick={handleSwitchAccount}
                    className="w-full mt-2 h-9 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a1b4b] hover:bg-[#292a73] text-white font-bold text-xs shadow-sm transition"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    <span>Sign Out & Switch Account</span>
                  </button>
                </div>
              )}

              {/* Case 2: Signed in with MATCHING email */}
              {isEmailMatching && (
                <button
                  onClick={handleAcceptExisting}
                  disabled={isSubmitting}
                  className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a1b4b] hover:bg-[#292a73] text-white font-bold text-sm shadow-md transition disabled:opacity-50"
                >
                  <span>{isSubmitting ? 'Joining workspace...' : 'Accept & Join Workspace'}</span>
                  <ArrowRight className="h-4 w-4 text-[#f7a400]" />
                </button>
              )}

              {/* Case 3: NOT logged in & Newly PROVISIONED employee -> Enter Temp Password + Set Permanent Password */}
              {!isAuthenticated && isProvisionedEmployee && (
                <form onSubmit={handleActivateProvisionedAccount} className="space-y-4 text-left">
                  <div>
                    <label className="block text-xs font-bold text-[#1a1b4b] mb-1">Temporary Password (from email)</label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3e4491]" />
                      <input
                        type="password"
                        required
                        value={temporaryPassword}
                        onChange={(e) => setTemporaryPassword(e.target.value)}
                        placeholder="Enter temporary password"
                        className="w-full h-10 pl-9 pr-3 bg-[#F8FAFC] border border-[#3e4491]/30 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3a9efd]/20 focus:border-[#3a9efd] transition text-[#1a1b4b]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#1a1b4b] mb-1">Create New Permanent Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3e4491]" />
                      <input
                        type="password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="w-full h-10 pl-9 pr-3 bg-[#F8FAFC] border border-[#3e4491]/30 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3a9efd]/20 focus:border-[#3a9efd] transition text-[#1a1b4b]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#1a1b4b] mb-1">Confirm New Password</label>
                    <div className="relative">
                      <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3e4491]" />
                      <input
                        type="password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full h-10 pl-9 pr-3 bg-[#F8FAFC] border border-[#3e4491]/30 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3a9efd]/20 focus:border-[#3a9efd] transition text-[#1a1b4b]"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-11 mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a1b4b] hover:bg-[#292a73] text-white font-bold text-sm shadow-md transition disabled:opacity-50"
                  >
                    <span>{isSubmitting ? 'Activating account...' : 'Activate & Enter Workspace'}</span>
                    <ArrowRight className="h-4 w-4 text-[#f7a400]" />
                  </button>
                </form>
              )}

              {/* Case 4: NOT logged in & EXISTING account -> Sign in with existing credentials */}
              {!isAuthenticated && !isProvisionedEmployee && (
                <div className="space-y-3">
                  <p className="text-xs text-[#3e4491]">
                    You already have an active Sahaj Connect account. Sign in to accept this workspace invitation.
                  </p>
                  <Link
                    to={`/login?redirect=/invite?token=${encodeURIComponent(token)}&email=${encodeURIComponent(invitation?.email || '')}`}
                    className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a1b4b] hover:bg-[#292a73] text-white font-bold text-sm shadow-md transition"
                  >
                    <LogIn className="h-4 w-4 text-[#f7a400]" />
                    <span>Sign In with Existing Account</span>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
